import React, { useEffect, useRef, useState } from 'react';
import { openSourceContributions } from '../data/mockData';
import { GitBranch, ExternalLink, Star, GitPullRequest } from 'lucide-react';
import { Badge } from './ui/badge';

const ContributionCard = ({ contribution, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="group p-6 rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/5 hover:border-emerald-200 dark:hover:border-emerald-500/20 transition-colors duration-300">
        <div className="flex items-start gap-4 mb-4">
          <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-500/10 flex-shrink-0">
            <GitBranch className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {contribution.org}
              </h3>
            </div>
            <p className="text-sm font-mono text-gray-500 dark:text-gray-400">
              {contribution.repo}
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
          {contribution.description}
        </p>

        {/* Issues */}
        <div className="space-y-2 mb-5">
          {contribution.issues.map((issue, i) => (
            <a
              key={i}
              href={issue.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-white/[0.02] border border-gray-100 dark:border-white/5 hover:border-emerald-200 dark:hover:border-emerald-500/20 transition-colors duration-200 group/link"
            >
              <GitPullRequest className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate">
                {issue.title}
              </span>
              <ExternalLink className="h-3.5 w-3.5 text-gray-400 group-hover/link:text-emerald-500 transition-colors" />
            </a>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {contribution.tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-0 text-xs font-normal"
            >
              {tag}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

const OpenSourceSection = () => {
  return (
    <section id="opensource" className="py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-12">
          <div className="h-px w-12 bg-emerald-500"></div>
          <h2 className="font-mono text-sm uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            Open Source
          </h2>
        </div>

        <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Open Source Contributions
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-12 max-w-xl">
          Contributing to projects that matter. Active participant in community-driven open source development and Hacktoberfest events.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {openSourceContributions.map((contrib, index) => (
            <ContributionCard key={contrib.id} contribution={contrib} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OpenSourceSection;